import React, { useState, useEffect, useRef } from 'react';
import { exportCSV, uid, grade, gradeColor } from '../db';
import { Badge, Btn, Card, CardHead, Field, Grid, Input, Modal, ModalFooter, NoData, Search, Select, Span, TblWrap, toast } from '../ui.jsx';
import { markIdCardGenerated } from '../storage';
import ReportCardStudio from '../ReportCardStudio';

function Exams({ db, save, logo, activeSessionId, teacher }) {
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ max: 100, st: 'Upcoming' });
  const [mkExam, setMkExam] = useState('');
  const [mkCls, setMkCls] = useState('');
  const [rcCls, setRcCls] = useState('');
  const [filterCls, setFilterCls] = useState('');
  const [filterSu, setFilterSu] = useState('');
  const [editId, setEditId] = useState(null);
  // Academic exam scheduling state
  const [acadOpen, setAcadOpen] = useState(false);
  const [acadCls, setAcadCls] = useState('');
  const [acadTest, setAcadTest] = useState('');
  const [acadSchedule, setAcadSchedule] = useState({}); // { subjectName: { dt, startTime, endTime } }

  const ACAD_TESTS = [
    { id: 'ut1', label: 'Unit Test 1', max: 20, term: 'Term1', rcKey: 't1_pt', divide: true },
    { id: 'hy',  label: 'Half Yearly', max: 80, term: 'Term1', rcKey: 't1_hy', divide: false },
    { id: 'ut2', label: 'Unit Test 2', max: 20, term: 'Term2', rcKey: 't2_pt', divide: true },
    { id: 'ye',  label: 'Yearly',      max: 80, term: 'Term2', rcKey: 't2_ye', divide: false },
  ];

  const sf = k => v => setForm(f => ({ ...f, [k]: v }));
  // All classes available regardless of teacher assignment
  const classes = db.classes.map(c => c.name);
  const subjects = [...new Set([
    ...db.classes.flatMap(c => (c.subs || '').split(',').map(s => s.trim()).filter(Boolean)),
    ...db.classes.flatMap(c => (c.coSubs || '').split(',').map(s => s.trim()).filter(Boolean)),
    ...db.exams.map(e => e.su).filter(Boolean),
  ])].sort();

  // Get all subjects (theory + co-scholastic) for a class
  const getClsSubjects = (clsName) => {
    const cls = db.classes.find(c => c.name === clsName);
    if (!cls) return { theory: [], co: [] };
    const theory = (cls.subs || '').split(',').map(s => s.trim()).filter(Boolean);
    const co = (cls.coSubs || 'General Knowledge, Art Education, Yoga / P.T.').split(',').map(s => s.trim()).filter(Boolean);
    return { theory, co };
  };

  // Grade from percentage (8-point scale)
  const grade8fromPct = pct => {
    if (pct >= 91) return 'A1'; if (pct >= 81) return 'A2'; if (pct >= 71) return 'B1';
    if (pct >= 61) return 'B2'; if (pct >= 51) return 'C1'; if (pct >= 41) return 'C2';
    if (pct >= 33) return 'D'; return 'E';
  };

  const openEdit = e => { setForm({ ...e }); setEditId(e.id); setOpen(true); };
  const openAdd = () => { setForm({ max: 100, st: 'Upcoming' }); setEditId(null); setOpen(true); };

  const saveAcadExams = () => {
    if (!acadCls || !acadTest) { toast('Select class and test type', 'err'); return; }
    const testDef = ACAD_TESTS.find(t => t.id === acadTest);
    if (!testDef) return;
    const { theory, co } = getClsSubjects(acadCls);
    const allSubs = [...theory.map(s => ({ s, isCoScho: false })), ...co.map(s => ({ s, isCoScho: true }))];
    const newExams = allSubs.map(({ s, isCoScho }) => {
      const sched = acadSchedule[s] || {};
      return {
        id: 'E' + uid(),
        name: testDef.label,
        cls: acadCls,
        su: s,
        suType: isCoScho ? 'co' : 'theory',
        max: testDef.max,
        term: testDef.term,
        rcKey: testDef.rcKey,
        divide: testDef.divide,
        isCoScho,
        dt: sched.dt || '',
        startTime: sched.startTime || '',
        endTime: sched.endTime || '',
        st: 'Upcoming',
        acadTestId: testDef.id,
      };
    });
    save({ ...db, exams: [...db.exams, ...newExams] });
    toast(`Scheduled ${newExams.length} exams for ${acadCls} — ${testDef.label}`);
    setAcadOpen(false); setAcadCls(''); setAcadTest(''); setAcadSchedule({});
  };

  const saveExam = () => {
    if (!form.name?.trim()) { toast('Name required', 'err'); return; }
    if (editId) {
      save({ ...db, exams: db.exams.map(e => e.id === editId ? { ...e, ...form, max: parseInt(form.max) || 100 } : e) });
      toast('Exam updated');
    } else {
      save({ ...db, exams: [...db.exams, { id: 'E' + uid(), ...form, max: parseInt(form.max) || 100 }] });
      toast('Exam added');
    }
    setForm({ max: 100, st: 'Upcoming' }); setEditId(null); setOpen(false);
  };
  const delExam = id => { if (!window.confirm('Delete?')) return; save({ ...db, exams: db.exams.filter(e => e.id !== id) }); };
  const setMark = (key, sid, val) => {
    const nm = { ...db.marks };
    if (val === '' || val === null || val === undefined) {
      if (nm[key]) { const k2 = { ...nm[key] }; delete k2[sid]; nm[key] = k2; }
    } else {
      nm[key] = { ...(nm[key] || {}), [sid]: parseFloat(val) };
    }
    // Auto-populate report card marks if this exam has rcKey (academic exam)
    const examId = key.split('_')[0];
    const examCls = key.split('_').slice(1).join('_');
    const ex = db.exams.find(e => e.id === examId);
    let updatedStudents = db.students;
    if (ex?.rcKey && val !== '' && val !== null && val !== undefined) {
      const numVal = parseFloat(val);
      const rcVal = ex.divide ? Math.ceil((numVal / ex.max) * 10) : numVal; // divide by 2 for UT (20→10), round up, keep for HY/Yearly
      if (ex.isCoScho) {
        // Co-scholastic: convert to grade
        const pct = (numVal / ex.max) * 100;
        const g = grade8fromPct(pct);
        updatedStudents = db.students.map(s => {
          if (s.id !== sid) return s;
          const coGrades = { ...(s._rcCoGrades || {}), [ex.su]: g };
          return { ...s, _rcCoGrades: coGrades };
        });
      } else {
        // Theory: store in _rcMarks
        updatedStudents = db.students.map(s => {
          if (s.id !== sid) return s;
          const rcMarks = { ...(s._rcMarks || {}) };
          rcMarks[ex.su] = { ...(rcMarks[ex.su] || {}), [ex.rcKey]: rcVal };
          return { ...s, _rcMarks: rcMarks };
        });
      }
    }
    save({ ...db, marks: nm, students: updatedStudents });
  };
  const getAttStats = sid => { let p = 0, t = 0; Object.values(db.att).forEach(d => { if (d[sid] !== undefined) { t++; if (d[sid] === 'P' || d[sid] === 'L') p++; } }); return { p, t }; };
  const genRC = () => {
    if (!rcCls) return;
    const sts = db.students.filter(s => s.cls === rcCls);
    if (!sts.length) { toast('No students', 'err'); return; }
    const school = db.settings.school || 'LORD KRISHNA PUBLIC SCHOOL';
    const yr = db.settings.reportAcademicYear || db.settings.year || '2025-2026';
    const addr = db.settings.addr || '';
    const prin = db.settings.prin || '';
    const phone = db.settings.phone || '';

    // 8-point grading scale
    const grade8 = pct => {
      if (pct >= 91) return 'A1';
      if (pct >= 81) return 'A2';
      if (pct >= 71) return 'B1';
      if (pct >= 61) return 'B2';
      if (pct >= 51) return 'C1';
      if (pct >= 41) return 'C2';
      if (pct >= 33) return 'D';
      return 'E';
    };

    // Subjects for this class
    const clsObj = db.classes.find(c => c.name === rcCls);
    const subjects = clsObj ? (clsObj.subs || '').split(',').map(s => s.trim()).filter(Boolean) : [];

    // Exams grouped by term
    const clsExams = db.exams.filter(e => !e.cls || e.cls === rcCls);
    const t1Exams = clsExams.filter(e => e.term === 'Term1');
    const t2Exams = clsExams.filter(e => e.term === 'Term2');

    // Helper: get marks for a student in an exam
    const getMark = (examId, sid) => {
      const key = examId + '_' + rcCls;
      const v = (db.marks[key] || {})[sid];
      return v !== undefined ? v : null;
    };

    // For each subject, find matching exams by subject name
    const getSubjectExams = (su, termExams) => termExams.filter(e => e.su === su);

    const css = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Inter',Arial,sans-serif;background:#e8edf2;color:#1a1a2e;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .page{width:210mm;min-height:297mm;margin:0 auto 20px;background:#fff;box-shadow:0 4px 32px rgba(0,0,0,0.15);position:relative;overflow:hidden;page-break-after:always}
      .page::before{content:'';position:absolute;left:0;top:0;bottom:0;width:6px;background:linear-gradient(180deg,#1a3a8f 0%,#2563eb 40%,#0ea5e9 100%)}
      .watermark{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);font-size:72px;font-weight:900;color:rgba(59,130,246,0.04);pointer-events:none;white-space:nowrap;z-index:0;letter-spacing:4px;text-transform:uppercase}
      .hdr{background:linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 55%,#0369a1 100%);padding:20px 28px 16px 34px;display:flex;align-items:center;gap:18px;position:relative;overflow:hidden}
      .hdr::after{content:'';position:absolute;right:-40px;top:-40px;width:180px;height:180px;border-radius:50%;background:rgba(255,255,255,0.06)}
      .hdr-logo{width:60px;height:60px;border-radius:50%;background:rgba(255,255,255,0.15);border:2px solid rgba(255,255,255,0.3);display:flex;align-items:center;justify-content:center;flex-shrink:0}
      .hdr-logo span{font-size:24px;font-weight:900;color:#fff;font-family:serif}
      .hdr-text{flex:1;text-align:center}
      .hdr-school{font-size:19px;font-weight:900;color:#fff;letter-spacing:1.5px;text-transform:uppercase;text-shadow:0 1px 4px rgba(0,0,0,0.3)}
      .hdr-addr{font-size:10px;color:rgba(255,255,255,0.72);margin-top:3px}
      .hdr-title{display:inline-block;margin-top:9px;background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);border-radius:20px;padding:3px 18px;font-size:12px;font-weight:700;color:#fff;letter-spacing:1px;text-transform:uppercase}
      .hdr-badge{flex-shrink:0;text-align:center}
      .hdr-badge-cls{background:rgba(255,255,255,0.18);border:1px solid rgba(255,255,255,0.32);border-radius:10px;padding:6px 14px}
      .hdr-badge-cls .lbl{font-size:9px;color:rgba(255,255,255,0.68);text-transform:uppercase;letter-spacing:0.8px;font-weight:600}
      .hdr-badge-cls .val{font-size:17px;font-weight:900;color:#fff;line-height:1.1}
      .hdr-badge-yr{font-size:10px;color:rgba(255,255,255,0.62);margin-top:5px}
      .info-strip{background:#f0f6ff;border-bottom:2px solid #dbeafe;padding:11px 28px 11px 34px;display:grid;grid-template-columns:1fr 1fr;gap:0 40px}
      .info-row{display:flex;align-items:baseline;padding:3.5px 0;border-bottom:1px dashed #dbeafe}
      .info-row:last-child{border-bottom:none}
      .info-lbl{font-size:10.5px;font-weight:700;color:#1e40af;min-width:115px;flex-shrink:0}
      .info-val{font-size:11px;color:#1a1a2e;font-weight:500}
      .tbl-wrap{padding-left:6px}
      .marks-tbl{width:100%;border-collapse:collapse;font-size:10.5px}
      .marks-tbl th,.marks-tbl td{border:1px solid #bfdbfe;text-align:center;padding:5px 3px;line-height:1.2}
      .th-area{background:linear-gradient(135deg,#1e3a8a,#2563eb);color:#fff;font-weight:800;font-size:11px;text-align:left;padding-left:10px}
      .th-term{background:linear-gradient(135deg,#1d4ed8,#3b82f6);color:#fff;font-weight:800;font-size:11.5px}
      .th-overall{background:linear-gradient(135deg,#0369a1,#0ea5e9);color:#fff;font-weight:800;font-size:11px}
      .th-sub{background:#dbeafe;color:#1e3a8a;font-weight:700;font-size:9.5px}
      .th-max{background:#fce7f3;color:#9d174d;font-weight:800;font-size:10px}
      .td-subj{text-align:left;padding-left:10px;font-weight:700;color:#1e3a8a;background:#f0f6ff;font-size:11px}
      .td-total{background:#dbeafe;font-weight:800;color:#1e3a8a}
      .td-grand{background:#dcfce7;font-weight:900;color:#166534;font-size:12px}
      .td-rank{font-weight:700;color:#7c3aed}
      tr:nth-child(even) td:not(.td-subj):not(.td-total):not(.td-grand){background:#f8faff}
      .g-a1{color:#166534;background:#dcfce7;border-radius:4px;padding:1px 5px;font-weight:900}
      .g-a2{color:#065f46;background:#d1fae5;border-radius:4px;padding:1px 5px;font-weight:900}
      .g-b1{color:#1e40af;background:#dbeafe;border-radius:4px;padding:1px 5px;font-weight:900}
      .g-b2{color:#1d4ed8;background:#eff6ff;border-radius:4px;padding:1px 5px;font-weight:900}
      .g-c1{color:#92400e;background:#fef3c7;border-radius:4px;padding:1px 5px;font-weight:900}
      .g-c2{color:#b45309;background:#fffbeb;border-radius:4px;padding:1px 5px;font-weight:900}
      .g-d{color:#c2410c;background:#ffedd5;border-radius:4px;padding:1px 5px;font-weight:900}
      .g-e{color:#991b1b;background:#fee2e2;border-radius:4px;padding:1px 5px;font-weight:900}
      .grade-note{background:#f8faff;border-top:1px solid #bfdbfe;border-bottom:1px solid #bfdbfe;padding:5px 28px 5px 34px;font-size:9px;color:#374151;font-style:italic;line-height:1.5}
      .totals-bar{background:linear-gradient(90deg,#f0f6ff,#e0f2fe);border-bottom:1px solid #bfdbfe;padding:9px 28px 9px 34px;display:flex;align-items:center;gap:20px}
      .tot-item{display:flex;align-items:center;gap:10px}
      .tot-lbl{font-size:11px;font-weight:700;color:#1e40af}
      .tot-val{background:#fff;border:2px solid #3b82f6;border-radius:8px;padding:3px 14px;font-size:13px;font-weight:900;color:#1e3a8a;box-shadow:0 2px 6px rgba(59,130,246,0.12)}
      .perf-bar-wrap{flex:1;margin-left:8px}
      .perf-bar-bg{height:8px;background:#dbeafe;border-radius:4px;overflow:hidden}
      .perf-bar-fill{height:100%;border-radius:4px}
      .perf-pct-lbl{font-size:9px;color:#6b7280;margin-top:2px;text-align:right}
      .co-wrap{display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #bfdbfe}
      .co-half:first-child{border-right:1px solid #bfdbfe}
      .co-tbl{width:100%;border-collapse:collapse;font-size:10.5px}
      .co-hdr-main{background:linear-gradient(90deg,#1d4ed8,#3b82f6);color:#fff;font-weight:800;font-size:11px;padding:6px 8px;text-align:center}
      .co-hdr-sub{background:#dbeafe;color:#1e3a8a;font-weight:700;font-size:10px;padding:4px 6px;border:1px solid #bfdbfe;text-align:center}
      .co-tbl td{border:1px solid #dbeafe;padding:4px 7px}
      .co-act{text-align:left;color:#1a1a2e}
      .co-g{text-align:center;font-weight:800;color:#1e40af}
      .footer{padding:9px 28px 12px 34px}
      .footer-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:5px}
      .att-badge{display:inline-flex;align-items:center;gap:6px;background:#f0f6ff;border:1px solid #bfdbfe;border-radius:6px;padding:4px 12px;font-weight:700;color:#1e40af;font-size:11px}
      .remark-badge{display:inline-flex;align-items:center;gap:6px;border-radius:6px;padding:4px 12px;font-weight:700;font-size:11px}
      .remark-good{background:#dcfce7;color:#166534;border:1px solid #bbf7d0}
      .remark-ok{background:#fef3c7;color:#92400e;border:1px solid #fde68a}
      .remark-poor{background:#fee2e2;color:#991b1b;border:1px solid #fecaca}
      .promo{font-size:11px;color:#374151;margin-bottom:8px;font-style:italic}
      .sig-row{display:flex;justify-content:space-between;padding-top:12px;border-top:1px solid #e5e7eb;margin-top:4px}
      .sig-item{text-align:center;min-width:110px}
      .sig-line{border-top:1.5px solid #374151;margin-top:26px;padding-top:4px;font-size:10px;color:#374151;font-weight:600}
      @media print{@page{size:A4;margin:6mm}body{background:#fff}.page{box-shadow:none;margin:0;page-break-after:always}}
    `;

    const gradeClass = g => ({ 'A1': 'g-a1', 'A2': 'g-a2', 'B1': 'g-b1', 'B2': 'g-b2', 'C1': 'g-c1', 'C2': 'g-c2', 'D': 'g-d', 'E': 'g-e' }[g] || '');
    const perfColor = pct => pct >= 75 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';

    const CO_ACTIVITIES = ['General Knowledge', 'Art Education', 'Health & Physical Education', 'Scientific Skills', 'Thinking Skills', 'Moral Education', 'Yoga / P.T.', 'Sports'];
    const DISCIPLINE = ['Regularity & Punctuality', 'Sincerity', 'Behavior & Values', 'Respectfulness for Rules & Regulations', 'Attitude Towards Teachers', 'Attitude Towards School-Mates', 'Attitude Towards Society', 'Attitude Towards Nation'];

    const w = window.open('', '_blank');
    let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Report Cards — ${rcCls}</title><style>${css}</style></head><body>`;

    sts.forEach(s => {
      const { p: ap, t: at } = getAttStats(s.id);

      // Build subject rows
      // For each subject, compute T1 and T2 component marks
      // We use exam names to detect components: Per.Test, Note Book, SEA, Half Yearly, Yearly Exam
      // If no structured exams, fall back to showing all exams as rows

      // Compute per-subject totals for T1 and T2
      const subjectRows = subjects.map(su => {
        const t1 = getSubjectExams(su, t1Exams);
        const t2 = getSubjectExams(su, t2Exams);

        // Try to find component exams by name keywords
        const findComp = (exams, keywords) => {
          const e = exams.find(ex => keywords.some(k => ex.name.toLowerCase().includes(k)));
          return e ? getMark(e.id, s.id) : null;
        };

        // T1 components
        const t1_pt = findComp(t1, ['per.test', 'periodic', 'per test', 'pt']);
        const t1_nb = findComp(t1, ['note', 'notebook', 'note book']);
        const t1_sea = findComp(t1, ['sea', 'sub enrich', 'enrichment']);
        const t1_hy = findComp(t1, ['half', 'half yearly', 'hy']);
        // T1 total from exam or sum
        const t1Total = t1.length === 1 ? getMark(t1[0].id, s.id) :
          (t1_pt !== null || t1_nb !== null || t1_sea !== null || t1_hy !== null)
            ? (t1_pt || 0) + (t1_nb || 0) + (t1_sea || 0) + (t1_hy || 0) : null;

        // T2 components
        const t2_pt = findComp(t2, ['per.test', 'periodic', 'per test', 'pt']);
        const t2_nb = findComp(t2, ['note', 'notebook', 'note book']);
        const t2_sea = findComp(t2, ['sea', 'sub enrich', 'enrichment']);
        const t2_ye = findComp(t2, ['yearly', 'annual', 'year exam', 'ye']);
        const t2Total = t2.length === 1 ? getMark(t2[0].id, s.id) :
          (t2_pt !== null || t2_nb !== null || t2_sea !== null || t2_ye !== null)
            ? (t2_pt || 0) + (t2_nb || 0) + (t2_sea || 0) + (t2_ye || 0) : null;

        // Grand total = T1 half + T2 half (50 each)
        const t1Max = t1.reduce((s, e) => s + (e.max || 0), 0) || 100;
        const t2Max = t2.reduce((s, e) => s + (e.max || 0), 0) || 100;
        const t1Half = t1Total !== null ? Math.round(t1Total / t1Max * 50) : null;
        const t2Half = t2Total !== null ? Math.round(t2Total / t2Max * 50) : null;
        const grand = (t1Half !== null && t2Half !== null) ? t1Half + t2Half : null;
        const grandPct = grand !== null ? Math.round(grand / 100 * 100) : null;

        return { su, t1_pt, t1_nb, t1_sea, t1_hy, t1Total, t2_pt, t2_nb, t2_sea, t2_ye, t2Total, t1Half, t2Half, grand, grandPct };
      });

      const validRows = subjectRows.filter(r => r.grand !== null);
      const overallTotal = validRows.reduce((s, r) => s + (r.grand || 0), 0);
      const overallMax = validRows.length * 100;
      const overallPct = overallMax > 0 ? Math.round(overallTotal / overallMax * 100) : 0;

      // Rank among class
      const classStudents = db.students.filter(st => st.cls === rcCls);
      const classScores = classStudents.map(st => {
        return subjects.reduce((sum, su) => {
          const t1 = getSubjectExams(su, t1Exams);
          const t2 = getSubjectExams(su, t2Exams);
          const t1Total = t1.length === 1 ? getMark(t1[0].id, st.id) : null;
          const t2Total = t2.length === 1 ? getMark(t2[0].id, st.id) : null;
          const t1Max = t1.reduce((s, e) => s + (e.max || 0), 0) || 100;
          const t2Max = t2.reduce((s, e) => s + (e.max || 0), 0) || 100;
          const t1H = t1Total !== null ? Math.round(t1Total / t1Max * 50) : 0;
          const t2H = t2Total !== null ? Math.round(t2Total / t2Max * 50) : 0;
          return sum + t1H + t2H;
        }, 0);
      }).sort((a, b) => b - a);
      const rank = classScores.indexOf(overallTotal) + 1;

      const fmt = v => v !== null && v !== undefined ? v : '—';
      const gBadge = g => g !== '—' ? `<span class="${gradeClass(g)}">${g}</span>` : '—';

      // Build subject table rows
      const subRows = subjectRows.map(r => {
        const g = r.grandPct !== null ? grade8(r.grandPct) : '—';
        return `<tr>
          <td class="td-subj">${r.su}</td>
          <td>${fmt(r.t1_pt)}</td><td>${fmt(r.t1_nb)}</td><td>${fmt(r.t1_sea)}</td><td>${fmt(r.t1_hy)}</td>
          <td class="td-total">${fmt(r.t1Total)}</td>
          <td>${fmt(r.t2_pt)}</td><td>${fmt(r.t2_nb)}</td><td>${fmt(r.t2_sea)}</td><td>${fmt(r.t2_ye)}</td>
          <td class="td-total">${fmt(r.t2Total)}</td>
          <td class="td-grand">${fmt(r.grand)}</td>
          <td style="font-weight:900;font-size:11px">${gBadge(g)}</td>
          <td class="td-rank">${rank > 0 && r.grand !== null ? rank : '—'}</td>
        </tr>`;
      }).join('');

      // Co-scholastic & discipline tables
      const coRows = CO_ACTIVITIES.map(a => `<tr><td class="co-act">${a}</td><td class="co-g">A2</td><td class="co-g">A1</td></tr>`).join('');
      const discRows = DISCIPLINE.map(d => `<tr><td class="co-act" style="font-size:10px">${d}</td><td class="co-g">A2</td><td class="co-g">A1</td></tr>`).join('');

      const remarkCls = overallPct >= 75 ? 'remark-good' : overallPct >= 50 ? 'remark-ok' : 'remark-poor';
      const remarkText = overallPct >= 75 ? 'Good!' : overallPct >= 50 ? 'Satisfactory' : 'Needs Improvement';
      const barColor = perfColor(overallPct);

      html += `
      <div class="page">
        <div class="watermark">${school}</div>

        <div class="hdr">
          <div class="hdr-logo"><span>${school.charAt(0)}</span></div>
          <div class="hdr-text">
            <div class="hdr-school">${school}</div>
            <div class="hdr-addr">${addr}${phone ? ' &nbsp;|&nbsp; Ph: ' + phone : ''}</div>
            <div class="hdr-title">Report Card</div>
          </div>
          <div class="hdr-badge">
            <div class="hdr-badge-cls"><div class="lbl">Class</div><div class="val">${rcCls}</div></div>
            <div class="hdr-badge-yr">Session: ${yr}</div>
          </div>
        </div>

        <div class="info-strip">
          <div>
            <div class="info-row"><span class="info-lbl">Student's Name</span><span class="info-val">${s.fn} ${s.ln}</span></div>
            <div class="info-row"><span class="info-lbl">Father's Name</span><span class="info-val">${s.father || '—'}</span></div>
            <div class="info-row"><span class="info-lbl">Mother's Name</span><span class="info-val">${s.mother || '—'}</span></div>
            <div class="info-row"><span class="info-lbl">Date of Birth</span><span class="info-val">${fmtDob(s.dob)}</span></div>
            <div class="info-row"><span class="info-lbl">Address</span><span class="info-val">${(s.addr && s.addr !== 'admin' ? s.addr : '') || addr || '—'}</span></div>
          </div>
          <div>
            <div class="info-row"><span class="info-lbl">Roll No.</span><span class="info-val">${s.roll || '—'}</span></div>
            <div class="info-row"><span class="info-lbl">Admission No.</span><span class="info-val">${s.admNo || '—'}</span></div>
            <div class="info-row"><span class="info-lbl">Gender</span><span class="info-val">${s.gn || '—'}</span></div>
            <div class="info-row"><span class="info-lbl">Blood Group</span><span class="info-val">${s.blood || '—'}</span></div>
          </div>
        </div>

        <div class="tbl-wrap">
          <table class="marks-tbl">
            <thead>
              <tr>
                <th rowspan="3" class="th-area" style="width:88px">Scholastic<br/>Area</th>
                <th colspan="5" class="th-term">Term 1 &nbsp;(100 Marks)</th>
                <th colspan="5" class="th-term">Term 2 &nbsp;(100 Marks)</th>
                <th colspan="3" class="th-overall">Overall</th>
              </tr>
              <tr>
                <th colspan="5" class="th-sub" style="font-size:8.5px;color:#6b7280">Per.Test + Notebook + SEA + Half Yearly</th>
                <th colspan="5" class="th-sub" style="font-size:8.5px;color:#6b7280">Per.Test + Notebook + SEA + Yearly Exam</th>
                <th colspan="3" class="th-sub">Term 1 (50) + Term 2 (50)</th>
              </tr>
              <tr class="th-sub">
                <th>Per.<br/>Test</th><th>Note<br/>Book</th><th>SEA</th><th>Half<br/>Yearly</th><th>Total</th>
                <th>Per.<br/>Test</th><th>Note<br/>Book</th><th>SEA</th><th>Yearly<br/>Exam</th><th>Total</th>
                <th>Grand<br/>Total</th><th>Grade</th><th>Rank</th>
              </tr>
              <tr class="th-max">
                <td class="td-subj" style="font-size:10px">Subjects</td>
                <td>10</td><td>5</td><td>5</td><td>80</td><td>100</td>
                <td>10</td><td>5</td><td>5</td><td>80</td><td>100</td>
                <td>100</td><td></td><td></td>
              </tr>
            </thead>
            <tbody>${subRows || '<tr><td colspan="14" style="text-align:center;padding:12px;color:#9ca3af;font-style:italic">No marks entered</td></tr>'}</tbody>
          </table>
        </div>

        <div class="grade-note">
          <strong>8-Point Grading Scale:</strong>&nbsp;
          A1 (91–100%) &nbsp;A2 (81–90%) &nbsp;B1 (71–80%) &nbsp;B2 (61–70%) &nbsp;C1 (51–60%) &nbsp;C2 (41–50%) &nbsp;D (33–40%) &nbsp;E (≤32%)
          &nbsp;&nbsp;<strong>*SEA</strong> = Sub Enrichment Activity
        </div>

        <div class="totals-bar">
          <div class="tot-item"><span class="tot-lbl">Overall Marks</span><span class="tot-val">${overallTotal} / ${overallMax}</span></div>
          <div class="tot-item"><span class="tot-lbl">Percentage</span><span class="tot-val">${overallPct}%</span></div>
          <div class="perf-bar-wrap">
            <div class="perf-bar-bg"><div class="perf-bar-fill" style="width:${overallPct}%;background:${barColor}"></div></div>
            <div class="perf-pct-lbl">${overallPct}% — ${remarkText}</div>
          </div>
        </div>
        <div class="co-wrap">
          <div class="co-half">
            <table class="co-tbl">
              <thead>
                <tr><td colspan="3" class="co-hdr-main">Co-Scholastic Areas</td></tr>
                <tr><th class="co-hdr-sub" style="text-align:left;padding-left:8px">Activity</th><th class="co-hdr-sub">T1</th><th class="co-hdr-sub">T2</th></tr>
              </thead>
              <tbody>${coRows}</tbody>
            </table>
          </div>
          <div class="co-half">
            <table class="co-tbl">
              <thead>
                <tr><td colspan="3" class="co-hdr-main">Discipline</td></tr>
                <tr><th class="co-hdr-sub" style="text-align:left;padding-left:8px">Element</th><th class="co-hdr-sub">T1</th><th class="co-hdr-sub">T2</th></tr>
              </thead>
              <tbody>${discRows}</tbody>
            </table>
          </div>
        </div>

        <div class="footer">
          <div class="footer-top">
            <span class="att-badge">&#128197; Attendance : ${ap} / ${at} days</span>
            <span class="remark-badge ${remarkCls}">Remarks : ${remarkText}</span>
          </div>
          <div class="promo">${overallPct >= 33 ? `&#10022; Congratulations! Promoted to Class ${rcCls}.` : '&#9888; Result: Detained — Please contact the school.'}</div>
          <div class="sig-row">
            <div class="sig-item"><div class="sig-line">Date : ${new Date().toLocaleDateString('en-IN')}</div></div>
            <div class="sig-item"><div class="sig-line">Class Teacher</div></div>
            <div class="sig-item"><div class="sig-line">Principal${prin ? ' (' + prin + ')' : ''}</div></div>
            <div class="sig-item"><div class="sig-line">Parent / Guardian</div></div>
          </div>
        </div>
      </div>`;
    });

    html += `<script>window.onload=()=>window.print()<\/script></body></html>`;
    w.document.write(html);
    w.document.close();
    toast('Report cards generated');

    // Save marksheet data to each student's record for later editing in Documents
    const updatedStudents = db.students.map(s => {
      if (s.cls !== rcCls) return s;
      const stuMarks = {};
      subjects.forEach(su => {
        const t1 = getSubjectExams(su, t1Exams);
        const t2 = getSubjectExams(su, t2Exams);
        const fc = (exams, kws) => { const e = exams.find(ex => kws.some(k => ex.name.toLowerCase().includes(k))); return e ? getMark(e.id, s.id) : null; };
        stuMarks[su] = {
          t1_pt: fc(t1, ['per.test', 'periodic', 'per test', 'pt']),
          t1_nb: fc(t1, ['note', 'notebook', 'note book']),
          t1_sea: fc(t1, ['sea', 'sub enrich', 'enrichment']),
          t1_hy: fc(t1, ['half', 'half yearly', 'hy']),
          t2_pt: fc(t2, ['per.test', 'periodic', 'per test', 'pt']),
          t2_nb: fc(t2, ['note', 'notebook', 'note book']),
          t2_sea: fc(t2, ['sea', 'sub enrich', 'enrichment']),
          t2_ye: fc(t2, ['yearly', 'annual', 'year exam', 'ye']),
        };
      });
      const { p: attP, t: attT } = getAttStats(s.id);
      return { ...s, _rcMarks: stuMarks, _rcSubjects: subjects.join(', '), _rcAttP: attP, _rcAttT: attT };
    });
    save({ ...db, students: updatedStudents });
  };

  // Filtered exams for schedule tab
  const filteredExams = db.exams.filter(e =>
    (!filterCls || e.cls === filterCls) &&
    (!filterSu || e.su === filterSu)
  );

  // Validation badge for grade entry
  const valBadge = (m, max) => {
    if (m === '' || m === undefined || m === null) return { label: 'Pending Input', cls: 'bg-surface-container-high text-on-surface-variant', icon: null };
    const pct = (parseFloat(m) / max) * 100;
    if (pct < 35) return { label: 'Below Threshold', cls: 'bg-error-container text-on-error-container', icon: 'warning' };
    return { label: 'Grade Calculated', cls: 'bg-secondary-container text-on-secondary-container', icon: 'check_circle' };
  };

  // Tick every 30s so status updates automatically as time passes
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const effectiveStatus = e => {
    const now = new Date();
    if (e.dt && e.startTime && e.endTime) {
      const start = new Date(e.dt + 'T' + e.startTime);
      const end = new Date(e.dt + 'T' + e.endTime);
      if (!isNaN(start) && !isNaN(end)) {
        if (now >= start && now <= end) return 'Ongoing';
        if (now > end) return 'Completed';
        return 'Upcoming';
      }
    } else if (e.dt && e.endTime) {
      const end = new Date(e.dt + 'T' + e.endTime);
      if (!isNaN(end)) return now > end ? 'Completed' : 'Upcoming';
    } else if (e.dt) {
      const day = new Date(e.dt); day.setHours(23, 59, 59);
      return day < now ? 'Completed' : 'Upcoming';
    }
    return e.st || 'Upcoming';
  };

  const statusPill = st => {
    if (st === 'Completed') return 'bg-green-100 text-green-800';
    if (st === 'Ongoing') return 'bg-blue-100 text-blue-800 animate-pulse';
    if (st === 'Upcoming') return 'bg-[#ffdeaa] text-[#5f4100]';
    return 'bg-secondary-container text-on-secondary-container';
  };

  const dayName = dt => { if (!dt) return ''; const d = new Date(dt); return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()]; };
  const dtParts = dt => { if (!dt) return { day: '—', mon: '—' }; const d = new Date(dt); return { day: d.getDate(), mon: d.toLocaleString('default', { month: 'short' }) }; };
  const fmtTime = t => { if (!t) return null; const [h, m] = t.split(':'); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`; };

  return (
    <div className="space-y-0">
      {/* Page Header */}
      <div className="px-10 pt-10 pb-6 space-y-1">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-on-surface-variant">Academic Session {db.settings.year || '2025-2026'}</p>
        <h1 className="text-5xl font-extrabold text-primary tracking-tight leading-none">Master Exam Schedule</h1>
        <p className="text-slate-500 text-lg font-body max-w-2xl">Centralized oversight of all scheduled assessments. Manage exams and enter student marks.</p>
      </div>

      {/* Tab switcher */}
      <div className="px-10 pb-0">
        <div className="flex gap-1 bg-surface-container-low p-1 rounded-xl w-fit">
          {['Schedule', 'Grade Entry Portal', 'Report Card'].map((t, i) => (
            <button key={i} onClick={() => setTab(i)}
              className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${tab === i ? 'bg-white text-primary shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* SCHEDULE TAB */}
      {tab === 0 && (
        <div className="px-10 py-8 space-y-8">
          {/* Filter & Action Bar */}
          <div className="flex flex-col md:flex-row gap-6 items-end justify-between bg-surface-container-low p-8 rounded-2xl">
            <div className="flex flex-wrap gap-6 items-end">
              <div className="space-y-2">
                <label className="block text-xs font-bold text-on-surface-variant tracking-wider uppercase">Filter by Class</label>
                <select value={filterCls} onChange={e => setFilterCls(e.target.value)}
                  className="bg-surface-container-low border border-outline-variant rounded-xl px-4 py-3 text-sm min-w-[160px] focus:ring-2 focus:ring-primary shadow-sm text-on-surface">
                  <option value="">All Classes</option>
                  {classes.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-xs font-bold text-on-surface-variant tracking-wider uppercase">Filter by Subject</label>
                <select value={filterSu} onChange={e => setFilterSu(e.target.value)}
                  className="bg-surface-container-low border border-outline-variant rounded-xl px-4 py-3 text-sm min-w-[180px] focus:ring-2 focus:ring-primary shadow-sm text-on-surface">
                  <option value="">All Subjects</option>
                  {subjects.map(s => <option key={s}>{s}</option>)}
                </select>              </div>
            </div>
            <button onClick={openAdd}
              className="flex items-center gap-2 bg-primary text-white px-6 py-3.5 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 active:translate-y-0">
              <span className="material-symbols-outlined text-lg">add_task</span>
              <span>Schedule New Exam</span>
            </button>
            <button onClick={() => setAcadOpen(true)}
              className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-3.5 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 active:translate-y-0">
              <span className="material-symbols-outlined text-lg">event_note</span>
              <span>Schedule Academic Exam</span>
            </button>
          </div>

          {/* Exam Table */}
          <div className="bg-surface-container-lowest rounded-3xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container border-b border-surface-container-high">
                    <th className="py-5 px-8 text-on-surface-variant font-label text-xs uppercase tracking-[0.1em]">Date &amp; Time</th>
                    <th className="py-5 px-6 text-on-surface-variant font-label text-xs uppercase tracking-[0.1em]">Class</th>
                    <th className="py-5 px-6 text-on-surface-variant font-label text-xs uppercase tracking-[0.1em]">Subject</th>
                    <th className="py-5 px-6 text-on-surface-variant font-label text-xs uppercase tracking-[0.1em]">Max Marks</th>
                    <th className="py-5 px-8 text-right text-on-surface-variant font-label text-xs uppercase tracking-[0.1em]">Status</th>
                    <th className="py-5 px-6 text-on-surface-variant font-label text-xs uppercase tracking-[0.1em]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredExams.map(e => {
                    const { day, mon } = dtParts(e.dt);
                    const st = effectiveStatus(e);
                    return (
                      <tr key={e.id} className="hover:bg-surface-container-low transition-colors group">
                        <td className="py-5 px-8">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-surface-container flex flex-col items-center justify-center">
                              <span className="text-[10px] font-bold text-slate-500 uppercase">{mon}</span>
                              <span className="text-lg font-black text-primary leading-none">{day}</span>
                            </div>
                            <div>
                              <p className="font-bold text-primary">{dayName(e.dt) || '—'}</p>
                              {e.startTime || e.endTime
                                ? <p className="text-xs text-slate-500">{fmtTime(e.startTime) || '?'} – {fmtTime(e.endTime) || '?'}</p>
                                : <p className="text-xs text-slate-500">{e.dt || 'No date set'}</p>
                              }
                            </div>
                          </div>
                        </td>
                        <td className="py-5 px-6">
                          <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100">{e.cls || 'All'}</span>
                        </td>
                        <td className="py-5 px-6">
                          <p className="font-bold text-primary">{e.name}</p>
                          <p className="text-xs text-slate-500">{e.su || '—'}</p>
                        </td>
                        <td className="py-5 px-6">
                          <p className="font-bold text-primary">{e.max}</p>
                        </td>
                        <td className="py-5 px-8 text-right">
                          <span className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${statusPill(st)}`}>{st}</span>
                        </td>
                        <td className="py-5 px-6">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEdit(e)} className="p-1.5 rounded-lg hover:bg-secondary-container text-on-surface-variant hover:text-on-secondary-container transition-colors">
                              <span className="material-symbols-outlined text-sm">edit</span>
                            </button>
                            <button onClick={() => delExam(e.id)} className="p-1.5 rounded-lg hover:bg-error-container text-on-surface-variant hover:text-on-error-container transition-colors">
                              <span className="material-symbols-outlined text-sm">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filteredExams.length && (
                    <tr><td colSpan={6} className="py-16 text-center text-on-surface-variant">
                      <span className="material-symbols-outlined text-4xl block mb-2 opacity-30">event_note</span>
                      No exams scheduled
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Cards */}
          <div className="grid grid-cols-1 pb-4">
            <div className="bg-primary-container p-8 rounded-[2rem] text-white flex flex-col justify-between">
              <div className="space-y-4">
                <p className="text-blue-300 text-xs font-bold uppercase tracking-widest">Schedule Summary</p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm opacity-80">Total Exams</span>
                    <span className="text-lg font-bold">{db.exams.length}</span>
                  </div>
                  <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-blue-400 h-full transition-all" style={{ width: db.exams.length ? Math.round(db.exams.filter(e => effectiveStatus(e) === 'Completed').length / db.exams.length * 100) + '%' : '0%' }}></div>
                  </div>
                  <div className="flex justify-between text-xs text-blue-200">
                    <span>{db.exams.filter(e => effectiveStatus(e) === 'Completed').length} Completed</span>
                    <span>{db.exams.filter(e => effectiveStatus(e) === 'Upcoming').length} Upcoming</span>
                  </div>
                </div>
              </div>
              <button onClick={() => { setTab(1); setMkCls(''); setMkExam(''); }}
                className="mt-8 w-full bg-white text-primary py-4 rounded-2xl font-black text-sm flex items-center justify-center gap-3 shadow-lg hover:shadow-primary/20 transition-all hover:scale-[1.02]">
                <span className="material-symbols-outlined">grade</span>
                Go to Grade Entry Portal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GRADE ENTRY PORTAL TAB */}
      {tab === 1 && (
        <div className="px-10 py-8 space-y-8">
          {/* Header */}
          <div className="flex justify-between items-end">
            <div>
              <nav className="flex items-center gap-2 text-xs font-bold tracking-widest text-on-surface-variant mb-3 uppercase">
                <span>{db.settings.year || '2025-2026'}</span>
                <span className="material-symbols-outlined text-[10px]">chevron_right</span>
                <span className="text-primary">Grade Entry Portal</span>
              </nav>
              <h2 className="text-4xl font-extrabold tracking-tight text-primary italic">Grade Entry Portal</h2>
              <p className="text-on-surface-variant font-medium text-base mt-1">
                {mkCls ? `${mkCls} • ${mkExam ? (db.exams.find(e => e.id === mkExam)?.name || '') : 'Select Exam'}` : 'Select class and exam to begin'}
              </p>
            </div>
            <div className="flex gap-6 items-end">
              <div className="text-right">
                <p className="text-xs font-bold text-on-surface-variant uppercase tracking-tighter">Students</p>
                <p className="text-primary font-bold">{mkCls ? db.students.filter(s => s.cls === mkCls).length : 0} Enrolled</p>
              </div>
            </div>
          </div>

          {/* Exam + Class selectors */}
          <div className="flex flex-wrap gap-4 items-end bg-surface-container-low p-6 rounded-2xl">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-on-surface-variant tracking-wider uppercase">Select Exam</label>
              <select value={mkExam} onChange={e => setMkExam(e.target.value)}
                className="bg-surface-container-low border border-outline-variant rounded-xl px-4 py-3 text-sm min-w-[200px] focus:ring-2 focus:ring-primary shadow-sm text-on-surface">
                <option value="">Choose Exam</option>
                {db.exams.map(e => <option key={e.id} value={e.id}>{e.name}{e.su ? ' – ' + e.su : ''}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-on-surface-variant tracking-wider uppercase">Select Class</label>
              <select value={mkCls} onChange={e => setMkCls(e.target.value)}
                className="bg-surface-container-low border border-outline-variant rounded-xl px-4 py-3 text-sm min-w-[140px] focus:ring-2 focus:ring-primary shadow-sm text-on-surface">
                <option value="">Choose Class</option>
                {classes.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            {mkExam && (() => {
              const ex = db.exams.find(e => e.id === mkExam);
              if (!ex?.rcKey) return null;
              const testDef = ACAD_TESTS.find(t => t.id === ex.acadTestId);
              const rcLabel = { t1_pt:'Term 1 Per.Test (÷2→/10)', t1_hy:'Term 1 Half Yearly (/80)', t2_pt:'Term 2 Per.Test (÷2→/10)', t2_ye:'Term 2 Yearly (/80)' }[ex.rcKey] || ex.rcKey;
              return (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm">
                  <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Auto-fills Report Card</div>
                  <div className="font-semibold text-emerald-800">{rcLabel}</div>
                  {ex.isCoScho && <div className="text-[10px] text-emerald-600 mt-0.5">Co-Scholastic → Grade auto-assigned</div>}
                </div>
              );
            })()}
          </div>

          {mkExam && mkCls ? (() => {
            const ex = db.exams.find(e => e.id === mkExam);
            const sts = db.students.filter(s => s.cls === mkCls);
            const key = mkExam + '_' + mkCls;
            const sv = db.marks[key] || {};
            return (
              <div className="grid grid-cols-12 gap-6">
                {/* Sidebar */}
                <aside className="col-span-12 lg:col-span-3 space-y-6">
                  <div className="bg-surface-container-low rounded-xl p-6">
                    <h4 className="text-xs font-black uppercase tracking-widest text-on-surface-variant mb-4">Grading Schema</h4>
                    <ul className="space-y-4">
                      <li className="flex justify-between items-center text-sm">
                        <span className="text-on-surface-variant">Max Marks</span>
                        <span className="font-bold text-primary">{ex.max}</span>
                      </li>
                      <li className="flex justify-between items-center text-sm">
                        <span className="text-on-surface-variant">Pass Mark</span>
                        <span className="font-bold text-primary">{Math.ceil(ex.max * 0.35)}</span>
                      </li>
                      <li className="flex justify-between items-center text-sm">
                        <span className="text-on-surface-variant">Distinction</span>
                        <span className="font-bold text-primary">{Math.ceil(ex.max * 0.75)}+</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-secondary-container/10 border border-secondary-container/20 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="material-symbols-outlined text-secondary" style={{ fontVariationSettings: "'FILL' 1" }}>info</span>
                      <h4 className="text-sm font-bold text-secondary">Manual Adjustments</h4>
                    </div>
                    <p className="text-xs leading-relaxed text-on-secondary-container font-medium">Use the ledger below for individual corrections. Changes are autosaved.</p>
                  </div>
                  <div className="bg-surface-container-low rounded-xl p-6 space-y-2">
                    <h4 className="text-xs font-black uppercase tracking-widest text-on-surface-variant mb-3">Class Stats</h4>
                    {(() => {
                      const entered = sts.filter(s => sv[s.id] !== undefined).length;
                      const below = sts.filter(s => sv[s.id] !== undefined && (sv[s.id] / ex.max) * 100 < 35).length;
                      return (<>
                        <div className="flex justify-between text-sm"><span className="text-on-surface-variant">Marks Entered</span><span className="font-bold text-primary">{entered}/{sts.length}</span></div>
                        <div className="flex justify-between text-sm"><span className="text-on-surface-variant">Below Threshold</span><span className="font-bold text-error">{below}</span></div>
                        <div className="w-full bg-surface-container-high h-1.5 rounded-full mt-2"><div className="bg-primary h-full rounded-full transition-all" style={{ width: sts.length ? (entered / sts.length * 100) + '%' : '0%' }}></div></div>
                      </>);
                    })()}
                  </div>
                </aside>

                {/* Main Ledger */}
                <div className="col-span-12 lg:col-span-9 bg-white rounded-xl overflow-hidden shadow-sm border border-surface-container">
                  <div className="p-6 border-b border-surface-container flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-primary">Student Performance Ledger</h2>
                    <span className="text-sm text-on-surface-variant">{ex.name} • Max {ex.max}</span>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-surface-container-low/50">
                        <th className="px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-on-surface-variant">Roll No. &amp; Student</th>
                        <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-on-surface-variant">Marks /{ex.max}</th>
                        <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-on-surface-variant">%</th>
                        <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-widest text-on-surface-variant">Validation</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-container">
                      {sts.map((s, i) => {
                        const m = sv[s.id] !== undefined ? sv[s.id] : '';
                        const pct = m !== '' ? Math.round((parseFloat(m) / ex.max) * 100) : null;
                        const vb = valBadge(m, ex.max);
                        const isBelowThreshold = m !== '' && pct < 35;
                        return (
                          <tr key={s.id} className="hover:bg-surface-container-low transition-colors duration-200">
                            <td className="px-8 py-5">
                              <div className="flex items-center gap-4">
                                <span className="text-xs font-bold text-on-surface-variant w-6">{String(i + 1).padStart(2, '0')}</span>
                                <div>
                                  <p className="font-bold text-primary">{s.fn} {s.ln}</p>
                                  <p className="text-[10px] text-on-surface-variant font-medium">{s.roll || s.admno || s.id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-5">
                              <input type="number" min="0" max={ex.max}
                                key={s.id + '_' + key}
                                defaultValue={m}
                                onBlur={ev => setMark(key, s.id, ev.target.value)}
                                placeholder="—"
                                className={`w-20 bg-surface-container-low border-none rounded-lg focus:ring-2 focus:ring-primary text-sm font-bold text-center py-2 ${isBelowThreshold ? 'ring-2 ring-error-container' : ''}`} />
                            </td>
                            <td className="px-6 py-5">
                              <span className={`text-lg font-extrabold ${m === '' ? 'text-outline-variant' : isBelowThreshold ? 'text-error' : 'text-primary'}`}>
                                {m !== '' ? pct + '%' : '—'}
                              </span>
                            </td>
                            <td className="px-6 py-5 text-right">
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${vb.cls}`}>
                                {vb.icon && <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: "'FILL' 1" }}>{vb.icon}</span>}
                                {vb.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="p-6 bg-surface-container-low flex justify-between items-center">
                    <p className="text-sm text-on-surface-variant font-medium">{sts.length} student entries</p>
                    <button onClick={() => toast('Marks saved')}
                      className="px-8 py-2.5 bg-primary text-white rounded-lg text-sm font-bold shadow-lg shadow-primary/20 hover:scale-105 transition-transform active:scale-95">
                      Save &amp; Finalize Marks
                    </button>
                  </div>
                </div>
              </div>
            );
          })() : (
            <div className="flex flex-col items-center justify-center py-20 text-on-surface-variant">
              <span className="material-symbols-outlined text-5xl mb-3 opacity-30">grade</span>
              <p className="font-medium">Select an exam and class to view the performance ledger</p>
            </div>
          )}
        </div>
      )}

      {/* REPORT CARD TAB */}
      {tab === 2 && (
        <div className="px-10 py-8">
          <ReportCardStudio db={db} save={save} logo={logo} activeSessionId={activeSessionId} />
        </div>
      )}

      {/* Academic Exam Scheduling Modal */}
      {acadOpen && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px',overflowY:'auto'}}>
          <div style={{background:'#fff',borderRadius:20,padding:28,width:'100%',maxWidth:700,boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
            <div style={{fontSize:18,fontWeight:800,color:'#1e293b',marginBottom:20}}>Schedule Academic Exam</div>

            {/* Step 1: Class */}
            <div style={{marginBottom:16}}>
              <label style={{display:'block',fontSize:10,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:6}}>Step 1 — Select Class</label>
              <select value={acadCls} onChange={e=>{setAcadCls(e.target.value);setAcadTest('');setAcadSchedule({});}}
                style={{width:'100%',padding:'10px 12px',borderRadius:10,border:'1.5px solid #e2e8f0',fontSize:13,outline:'none',background:'#fff'}}>
                <option value="">— Choose Class —</option>
                {classes.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>

            {/* Step 2: Test Type */}
            {acadCls && (
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:10,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:6}}>Step 2 — Select Test</label>
                <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8}}>
                  {ACAD_TESTS.map(t=>(
                    <button key={t.id} type="button" onClick={()=>{setAcadTest(t.id);setAcadSchedule({});}}
                      style={{padding:'12px',borderRadius:10,border:`2px solid ${acadTest===t.id?'#1960a3':'#e2e8f0'}`,background:acadTest===t.id?'#eef4ff':'#f8fafc',cursor:'pointer',textAlign:'left',transition:'all 150ms'}}>
                      <div style={{fontSize:13,fontWeight:700,color:acadTest===t.id?'#1960a3':'#1e293b'}}>{t.label}</div>
                      <div style={{fontSize:11,color:'#64748b',marginTop:2}}>{t.max} Marks · {t.term}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Subject schedule */}
            {acadCls && acadTest && (()=>{
              const {theory,co} = getClsSubjects(acadCls);
              const testDef = ACAD_TESTS.find(t=>t.id===acadTest);
              const allSubs = [...theory.map(s=>({s,isCoScho:false})),...co.map(s=>({s,isCoScho:true}))];
              return (
                <div style={{marginBottom:16}}>
                  <label style={{display:'block',fontSize:10,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:10}}>
                    Step 3 — Set Date & Time per Subject ({testDef.max} Marks each)
                  </label>
                  <div style={{maxHeight:320,overflowY:'auto',border:'1px solid #e2e8f0',borderRadius:12}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 150px 120px 120px',gap:8,padding:'8px 14px',background:'#f8fafc',borderBottom:'1px solid #e2e8f0',borderRadius:'12px 12px 0 0'}}>
                      <span style={{fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase'}}>Subject</span>
                      <span style={{fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase'}}>Date</span>
                      <span style={{fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase'}}>Start Time</span>
                      <span style={{fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase'}}>End Time</span>
                    </div>
                    {allSubs.map(({s,isCoScho},i)=>(
                      <div key={s} style={{display:'grid',gridTemplateColumns:'1fr 150px 120px 120px',gap:8,alignItems:'center',padding:'10px 14px',borderBottom:i<allSubs.length-1?'1px solid #f1f5f9':'none',background:i%2===0?'#fff':'#fafbfc'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <span style={{fontSize:12,fontWeight:600,color:'#1e293b'}}>{s}</span>
                          {isCoScho && <span style={{fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:20,background:'#dcfce7',color:'#059669'}}>Co-Sch</span>}
                        </div>
                        <input type="date" value={acadSchedule[s]?.dt||''} onChange={e=>setAcadSchedule(p=>({...p,[s]:{...p[s],dt:e.target.value}}))}
                          style={{padding:'7px 10px',borderRadius:8,border:'1.5px solid #e2e8f0',fontSize:12,outline:'none',width:'100%'}}/>
                        <input type="time" placeholder="Start" value={acadSchedule[s]?.startTime||''} onChange={e=>setAcadSchedule(p=>({...p,[s]:{...p[s],startTime:e.target.value}}))}
                          style={{padding:'7px 10px',borderRadius:8,border:'1.5px solid #e2e8f0',fontSize:12,outline:'none',width:'100%'}}/>
                        <input type="time" placeholder="End" value={acadSchedule[s]?.endTime||''} onChange={e=>setAcadSchedule(p=>({...p,[s]:{...p[s],endTime:e.target.value}}))}
                          style={{padding:'7px 10px',borderRadius:8,border:'1.5px solid #e2e8f0',fontSize:12,outline:'none',width:'100%'}}/>
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:11,color:'#94a3b8',marginTop:6}}>Date and time are optional — you can fill them later. Status auto-updates based on time.</div>
                </div>
              );
            })()}

            <div style={{display:'flex',gap:10,marginTop:8}}>
              <button onClick={()=>{setAcadOpen(false);setAcadCls('');setAcadTest('');setAcadSchedule({});}}
                style={{flex:1,padding:'11px',borderRadius:10,border:'1.5px solid #e2e8f0',background:'transparent',color:'#64748b',fontWeight:700,fontSize:13,cursor:'pointer'}}>Cancel</button>
              <button onClick={saveAcadExams} disabled={!acadCls||!acadTest}
                style={{flex:2,padding:'11px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#059669,#10b981)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',opacity:(!acadCls||!acadTest)?0.5:1}}>
                Schedule Exams
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Exam Modal */}
      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? 'Edit Exam' : 'Schedule New Exam'}>
        <Grid>
          <Span><Field label="Name *"><Input value={form.name || ''} onChange={sf('name')} placeholder="Enter exam name" /></Field></Span>
          <Field label="Class"><Select value={form.cls || ''} onChange={v => { sf('cls')(v); sf('su')(''); sf('suType')('theory'); }}><option value="">All</option>{classes.map(c => <option key={c}>{c}</option>)}</Select></Field>
          <Field label="Subject Type"><Select value={form.suType || 'theory'} onChange={v => { sf('suType')(v); sf('su')(''); }}>
            <option value="theory">Theory</option>
            <option value="co">Co-Scholastic</option>
          </Select></Field>
          <Field label="Subject"><Select value={form.su || ''} onChange={sf('su')}><option value="">— Select Subject —</option>
            {(() => {
              const cls = form.cls ? db.classes.find(c => c.name === form.cls) : null;
              const type = form.suType || 'theory';
              if (cls) {
                const src = type === 'co' ? (cls.coSubs || '') : (cls.subs || '');
                return src.split(',').map(s => s.trim()).filter(Boolean).map(s => <option key={s} value={s}>{s}</option>);
              }
              // fallback: all subjects
              return subjects.map(s => <option key={s} value={s}>{s}</option>);
            })()}
          </Select></Field>
          <Field label="Date"><Input type="date" value={form.dt || ''} onChange={sf('dt')} /></Field>
          <Field label="Start Time"><Input type="time" value={form.startTime || ''} onChange={sf('startTime')} /></Field>
          <Field label="End Time"><Input type="time" value={form.endTime || ''} onChange={sf('endTime')} /></Field>
          <Field label="Max Marks"><Input type="number" value={form.max || 100} onChange={sf('max')} placeholder="100" /></Field>
          <Field label="Term"><Select value={form.term || ''} onChange={sf('term')}><option value="">— None —</option><option value="Term1">Term 1</option><option value="Term2">Term 2</option></Select></Field>
        </Grid>
        <ModalFooter><Btn onClick={() => { setOpen(false); setEditId(null); }}>Cancel</Btn><Btn variant="primary" onClick={saveExam}>{editId ? 'Update' : 'Save'}</Btn></ModalFooter>
      </Modal>
    </div>
  );
}


export default Exams;
